import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { PasswordField } from "@/components/auth/PasswordField";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/features/auth/AuthProvider";
import { LegalDocumentDialog } from "@/features/legal/LegalDocumentDialog";
import { fetchLegalDocument, sha256Hex } from "@/features/legal/legalDocuments";
import type { LegalDocType } from "@/features/legal/legal-constants";
import { setRememberMe } from "@/lib/supabaseClient";

/**
 * SPEC-16 §7 — sliding login ↔ signup form, controlled by parent.
 *
 * The two panels share a horizontal track that translates via CSS
 * `transform` so the transition is GPU-accelerated and we avoid pulling
 * in framer-motion. Tabs are rendered on top of the track and act as
 * the human-visible controls; the parent (LoginPage) also drives `mode`
 * via the hero's CTA buttons.
 *
 * SPEC-27 — the login panel hosts a local "forgot" sub-view that
 * replaces its contents (single email field + send button). It does
 * NOT disturb the login↔signup slide track; switching tabs always
 * snaps the login panel back to the credentials view.
 */

type Mode = "login" | "signup";
type LoginView = "login" | "forgot";
type SignupView = "form" | "sent";

interface AuthFormProps {
  mode: Mode;
  onModeChange: (mode: Mode) => void;
  onAuthed?: () => void;
}

const loginSchema = z.object({
  email: z.string().email("Geçerli bir e-posta adresi girin."),
  password: z.string().min(6, "Şifre en az 6 karakter olmalı."),
});

const signupSchema = z.object({
  fullName: z.string().min(2, "Ad Soyad en az 2 karakter olmalı."),
  email: z.string().email("Geçerli bir e-posta adresi girin."),
  password: z.string().min(6, "Şifre en az 6 karakter olmalı."),
});

const forgotSchema = z.object({
  email: z.string().email("Geçerli bir e-posta adresi girin."),
});

type LoginValues = z.infer<typeof loginSchema>;
type SignupValues = z.infer<typeof signupSchema>;
type ForgotValues = z.infer<typeof forgotSchema>;

export function AuthForm({ mode, onModeChange, onAuthed }: AuthFormProps) {
  const { signIn, signUp, requestPasswordReset } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loginView, setLoginView] = useState<LoginView>("login");
  const [signupView, setSignupView] = useState<SignupView>("form");
  const [sentToEmail, setSentToEmail] = useState<string>("");
  // SPEC-DOCCONSENT-2 — affirmative legal acceptance. MUST default
  // false; returning to the signup tab also resets it (see
  // handleModeChange) so re-display always demands a fresh check.
  const [agreed, setAgreed] = useState(false);
  // Which legal document the popup is currently showing (`null` =
  // closed). Setting this opens `LegalDocumentDialog`, which lazy-loads
  // the text.
  const [docOpen, setDocOpen] = useState<LegalDocType | null>(null);

  const loginForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const signupForm = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { fullName: "", email: "", password: "" },
  });
  const forgotForm = useForm<ForgotValues>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: "" },
  });

  const onLoginSubmit = loginForm.handleSubmit(async (values) => {
    try {
      // Route the session token Supabase is about to issue to either
      // localStorage (remember) or sessionStorage (don't). MUST run
      // before signInWithPassword so the first setItem from the auth
      // flow already lands in the right store.
      setRememberMe(remember);
      await signIn(values.email, values.password);
      toast.success("Giriş başarılı");
      onAuthed?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Giriş başarısız: ${message}`);
    }
  });

  const onSignupSubmit = signupForm.handleSubmit(async (values) => {
    // SPEC-DOCCONSENT-2 — the submit button is disabled while !agreed,
    // but be defensive in case a future refactor unwires that gating;
    // never run the network round-trip without affirmative consent.
    if (!agreed) return;
    try {
      // Fetch the CURRENT legal documents + recompute their SHA-256 in
      // the browser so the metadata we attach is the bytes the user's
      // browser actually saw. The DB trigger still writes the SERVER
      // hash; a later audit can compare the two for any drift.
      const [tos, privacy] = await Promise.all([
        fetchLegalDocument("terms_of_service"),
        fetchLegalDocument("privacy_notice"),
      ]);
      if (!tos || !privacy) {
        toast.error(
          "Sözleşme metinleri yüklenemedi, lütfen tekrar deneyin.",
        );
        return;
      }
      const termsAcceptance = {
        accepted: true as const,
        tos_version: tos.version,
        tos_hash: await sha256Hex(tos.content),
        privacy_version: privacy.version,
        privacy_hash: await sha256Hex(privacy.content),
        user_agent: navigator.userAgent.slice(0, 500),
        source: "ifrs16" as const,
      };
      const { needsConfirmation } = await signUp(
        values.email,
        values.password,
        values.fullName,
        termsAcceptance,
      );
      if (needsConfirmation) {
        // SPEC-28 — Confirm email is on; replace the form with an
        // inline "verify your inbox" panel that names the address.
        setSentToEmail(values.email);
        setSignupView("sent");
        // SPEC-DOCCONSENT-2 — re-arm the affirmative-consent gate for
        // any later return to the signup form.
        setAgreed(false);
        return;
      }
      toast.success("Kayıt başarılı");
      onAuthed?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Kayıt başarısız: ${message}`);
    }
  });

  const onForgotSubmit = forgotForm.handleSubmit(async (values) => {
    // SPEC-27 §3 — show the SAME neutral toast on resolved and
    // rejected paths so we never reveal which addresses are
    // registered. Errors are logged for the developer console only.
    const neutralToast = () =>
      toast.success(
        "Eğer bu e-posta kayıtlıysa, sıfırlama bağlantısı gönderildi.",
      );
    try {
      await requestPasswordReset(values.email);
    } catch (err) {
      console.warn("[auth] requestPasswordReset failed", err);
    } finally {
      neutralToast();
      forgotForm.reset();
      setLoginView("login");
    }
  });

  function handleModeChange(next: Mode): void {
    // Always snap both sub-views back to their primary form when the
    // user toggles tabs; otherwise returning to a tab would surprise
    // them with the forgot or sent panel from earlier.
    setLoginView("login");
    setSignupView("form");
    // SPEC-DOCCONSENT-2 — clear the affirmative-consent state on every
    // tab toggle so re-entering the signup tab always demands a fresh
    // check (KVKK / e-sözleşme: acceptance is never sticky).
    setAgreed(false);
    onModeChange(next);
  }

  const trackTransform =
    mode === "login" ? "translateX(0%)" : "translateX(-50%)";

  const loginSubmitting = loginForm.formState.isSubmitting;
  const signupSubmitting = signupForm.formState.isSubmitting;
  const forgotSubmitting = forgotForm.formState.isSubmitting;

  const cardTitle =
    mode === "signup"
      ? signupView === "sent"
        ? "E-postanızı doğrulayın"
        : "Hesap oluşturun"
      : loginView === "forgot"
        ? "Şifre sıfırlama"
        : "Tekrar hoş geldiniz";
  const cardDescription =
    mode === "signup"
      ? signupView === "sent"
        ? "Hesabınızı etkinleştirmek için gelen kutunuzu kontrol edin."
        : "Birkaç saniye içinde hesabınızı oluşturun."
      : loginView === "forgot"
        ? "E-posta adresinize bir sıfırlama bağlantısı göndereceğiz."
        : "Hesabınıza giriş yaparak çalışmanıza devam edin.";

  return (
    <Card className="w-full max-w-md border-border/60 shadow-xl">
      <CardHeader className="space-y-3 pb-4 text-center">
        <CardTitle className="text-2xl">{cardTitle}</CardTitle>
        <CardDescription>{cardDescription}</CardDescription>
        <Tabs
          value={mode}
          onValueChange={(v) => handleModeChange(v as Mode)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Giriş Yap</TabsTrigger>
            <TabsTrigger value="signup">Kayıt Ol</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden">
          <div
            className="flex w-[200%] transition-transform duration-500 ease-in-out"
            style={{ transform: trackTransform }}
          >
            {/* ── Login panel (with optional forgot sub-view) ──────── */}
            <div
              className="w-1/2 shrink-0 pr-2"
              aria-hidden={mode !== "login"}
            >
              {loginView === "login" ? (
                <form
                  onSubmit={onLoginSubmit}
                  className="space-y-5"
                  noValidate
                >
                  <div className="space-y-2">
                    <Label htmlFor="login-email">E-posta</Label>
                    <Input
                      id="login-email"
                      type="email"
                      autoComplete="email"
                      placeholder="ornek@firma.com"
                      {...loginForm.register("email")}
                    />
                    {loginForm.formState.errors.email ? (
                      <p className="text-sm text-destructive">
                        {loginForm.formState.errors.email.message}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Şifre</Label>
                    <PasswordField
                      id="login-password"
                      autoComplete="current-password"
                      showPassword={showPassword}
                      onToggle={() => setShowPassword((s) => !s)}
                      register={loginForm.register("password")}
                    />
                    {loginForm.formState.errors.password ? (
                      <p className="text-sm text-destructive">
                        {loginForm.formState.errors.password.message}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="login-remember"
                        checked={remember}
                        onCheckedChange={(v) => setRemember(v === true)}
                      />
                      <Label
                        htmlFor="login-remember"
                        className="cursor-pointer text-sm font-normal"
                      >
                        Beni hatırla
                      </Label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLoginView("forgot")}
                      className="text-sm text-primary underline-offset-2 hover:underline"
                      data-testid="login-forgot-link"
                    >
                      Şifremi unuttum?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loginSubmitting}
                  >
                    {loginSubmitting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Giriş Yap
                  </Button>
                </form>
              ) : (
                <form
                  onSubmit={onForgotSubmit}
                  className="space-y-5"
                  noValidate
                  data-testid="forgot-form"
                >
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">E-posta</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      autoComplete="email"
                      placeholder="ornek@firma.com"
                      {...forgotForm.register("email")}
                    />
                    {forgotForm.formState.errors.email ? (
                      <p className="text-sm text-destructive">
                        {forgotForm.formState.errors.email.message}
                      </p>
                    ) : null}
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={forgotSubmitting}
                  >
                    {forgotSubmitting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Sıfırlama bağlantısı gönder
                  </Button>

                  <button
                    type="button"
                    onClick={() => {
                      forgotForm.reset();
                      setLoginView("login");
                    }}
                    className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                    data-testid="forgot-back-link"
                  >
                    <ArrowLeft className="h-3 w-3" />
                    Giriş'e dön
                  </button>
                </form>
              )}
            </div>

            {/* ── Signup panel (with optional sent sub-view) ───────── */}
            <div
              className="w-1/2 shrink-0 pl-2"
              aria-hidden={mode !== "signup"}
            >
              {signupView === "form" ? (
                <form
                  onSubmit={onSignupSubmit}
                  className="space-y-5"
                  noValidate
                >
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Ad Soyad</Label>
                    <Input
                      id="signup-name"
                      autoComplete="name"
                      placeholder="Adınız Soyadınız"
                      {...signupForm.register("fullName")}
                    />
                    {signupForm.formState.errors.fullName ? (
                      <p className="text-sm text-destructive">
                        {signupForm.formState.errors.fullName.message}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">E-posta</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      autoComplete="email"
                      placeholder="ornek@firma.com"
                      {...signupForm.register("email")}
                    />
                    {signupForm.formState.errors.email ? (
                      <p className="text-sm text-destructive">
                        {signupForm.formState.errors.email.message}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Şifre</Label>
                    <PasswordField
                      id="signup-password"
                      autoComplete="new-password"
                      showPassword={showPassword}
                      onToggle={() => setShowPassword((s) => !s)}
                      register={signupForm.register("password")}
                    />
                    {signupForm.formState.errors.password ? (
                      <p className="text-sm text-destructive">
                        {signupForm.formState.errors.password.message}
                      </p>
                    ) : null}
                  </div>

                  {/* SPEC-DOCCONSENT-2 — affirmative-consent row.
                      Defaults UNCHECKED; the two bracketed labels are
                      link-style buttons that open the LegalDocumentDialog
                      with the correct doc type.
                      SPEC-DOCCONSENT-2b — sentence flows as a single
                      paragraph. Uses a NATIVE lowercase <label> (not the
                      shadcn <Label>, which is `flex items-center` and
                      would force each inline button onto its own
                      flex-item row). `items-start + mt-0.5` keeps the
                      checkbox aligned with the first line of the wrapped
                      sentence. */}
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="signup-agree"
                      checked={agreed}
                      onCheckedChange={(v) => setAgreed(v === true)}
                      data-testid="signup-agree-checkbox"
                      className="mt-0.5"
                    />
                    <label
                      htmlFor="signup-agree"
                      className="cursor-pointer text-sm leading-relaxed"
                    >
                      <button
                        type="button"
                        onClick={() => setDocOpen("terms_of_service")}
                        data-testid="signup-tos-link"
                        className="text-primary underline underline-offset-2 hover:text-primary/80"
                      >
                        Kullanıcı Sözleşmesi
                      </button>
                      'ni ve{" "}
                      <button
                        type="button"
                        onClick={() => setDocOpen("privacy_notice")}
                        data-testid="signup-privacy-link"
                        className="text-primary underline underline-offset-2 hover:text-primary/80"
                      >
                        Aydınlatma Metni
                      </button>
                      'ni okudum, anladım, onaylıyorum.
                    </label>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={signupSubmitting || !agreed}
                  >
                    {signupSubmitting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Kayıt Ol
                  </Button>
                </form>
              ) : (
                <div
                  className="flex flex-col items-center gap-4 py-4 text-center"
                  data-testid="signup-sent-panel"
                >
                  <MailCheck className="h-10 w-10 text-primary" />
                  <p className="text-sm text-muted-foreground">
                    <span
                      className="block break-all font-medium text-foreground"
                      data-testid="signup-sent-email"
                    >
                      {sentToEmail}
                    </span>
                    adresine bir doğrulama bağlantısı gönderdik. Hesabınızı
                    etkinleştirmek için bağlantıya tıklayın.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setSignupView("form");
                      signupForm.reset();
                      handleModeChange("login");
                    }}
                    data-testid="signup-sent-back"
                  >
                    Giriş'e dön
                  </Button>
                </div>
              )}
              {/* SPEC-DOCCONSENT-2 — single Dialog mount covering both
                  links. Lazy-loads the chosen document when `docOpen`
                  flips from null. Rendered inside the signup panel so
                  it's only mounted while the user is on signup. */}
              <LegalDocumentDialog
                type={docOpen}
                open={docOpen !== null}
                onClose={() => setDocOpen(null)}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
